import { Platform } from 'react-native';
import { createSelector } from 'reselect';
import get from 'lodash/get';

import { RootState } from 'store/types';
import { FeatureFlags } from 'store/appSettings/types';

export const settingsSelector = (state: RootState) => state.appSettings;

export const mobileSettingsSelector = createSelector(settingsSelector, settings => settings.mobile);

export const sharedSettingsSelector = createSelector(settingsSelector, settings => settings.shared);

export const featureFlagsSelector = createSelector(
  sharedSettingsSelector,
  settings => settings.featureFlags || {}
);

export const isFeatureFlagOn = (flagKey: string, featureFlags: FeatureFlags) => {
  const featureFlag = featureFlags[flagKey];
  if (!featureFlag) {
    return false;
  }
  if (featureFlag.enabled) {
    return true;
  }
  const now = new Date();
  if (featureFlag.on) {
    const onDate = new Date(featureFlag.on);
    if (now < onDate) {
      return false;
    }
    if (featureFlag.off) {
      const offDate = new Date(featureFlag.off);
      return now < offDate;
    }
    return true;
  }
  if (featureFlag.off) {
    const offDate = new Date(featureFlag.off);
    return now < offDate;
  }
  return false;
};

export const isFeatureFlagOnSelector = (state: RootState) => {
  return (flag: string) => {
    return isFeatureFlagOn(flag, featureFlagsSelector(state));
  };
};

export const auditServiceSelector = createSelector(
  sharedSettingsSelector,
  shared => shared.audit_service
);

export const settingsErrorSelector = createSelector(settingsSelector, settings => settings.error);

export const releaseNotesSelector = createSelector(
  mobileSettingsSelector,
  (mobileSettings): string => mobileSettings.app_support.release_notes
);

export const minSupportVersionSelector = createSelector(
  mobileSettingsSelector,
  (mobileSettings): boolean =>
    Platform.select({
      ios: get(mobileSettings, 'app_support.ios_minimum_support_version', '0.0.0'),
      android: get(mobileSettings, 'app_support.android_minimum_support_version', '0.0.0')
    })
);

export const apiKeySelector = createSelector(sharedSettingsSelector, (sharedSettings): string => {
  const apiKey = get(sharedSettings, 'apikey');

  return apiKey;
});

export const encryptionSettingsSelector = createSelector(sharedSettingsSelector, (sharedSettings): {
  algorithm: string;
  password: string;
} => ({
  algorithm: sharedSettings.encryption_algorithm,
  password: sharedSettings.encryption_password
}));

export const billingEncryptionSettingsSelector = (state: RootState) =>
  sharedSettingsSelector(state).billing;

export const appErrorsSelector = createSelector(sharedSettingsSelector, state => state.errors);

export const settingsLoadingSelector = createSelector(settingsSelector, state => state.isFetching);

export const settingsLoadedSelector = createSelector(settingsSelector, state => state.loaded);
